/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { TextBox as DXTextBox } from 'devextreme-react/text-box';
import { Validator, RequiredRule, CustomRule } from 'devextreme-react/validator';
import '../../../scss/controls/_textbox.scss';
import { FocusInEvent, KeyUpEvent, ValueChangedEvent } from 'devextreme/ui/text_box';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { InfoIconButton } from '../InfoIconButton';

export enum TextBoxComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

interface TextBoxProps {
    id?: string;
    dataTestid?: string;
    value: string | undefined;
    name?: string;
    type?: 'email' | 'password' | 'search' | 'tel' | 'text' | 'url';
    readOnly?: boolean;
    disabled?: boolean;
    label?: string;
    helperText?: React.ReactNode;
    required?: boolean;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    width?: string;
    maxLength?: number;
    mask?: string;
    maskChar?: string;
    maskInvalidMessage?: string;
    onChange?: (value: string) => void;
    onChangeWithMask?: (value?: string) => void;
    onEnterKey?: () => void;
    onKeyUp?: ((e: KeyUpEvent) => void) | undefined;
    onFocusIn?: ((e: FocusInEvent) => void) | undefined;
    validateValue?: (value: string) => boolean;
    onValidationEvent?: (isValid: boolean) => void;
    validateValueMessage?: string;
    placeholder?: string;
    componentSpacing?: TextBoxComponentSpacing;
    showInfoIcon?: boolean;
    infoText?: string;
}

// Allowing "any" because it is defined by DevExtreme
export interface ValidationCallbackOptions {
    column: Record<string, any>;
    data: Record<string, any>;
    formItem: Record<string, any>;
    rule: Record<string, any>;
    validator: Record<string, any>;
    value: string | number;
}

export const TextBox: React.FC<TextBoxProps> = ({
    id,
    dataTestid,
    value,
    mask,
    maskChar,
    maskInvalidMessage,
    type = 'text',
    readOnly = false,
    disabled = false,
    label,
    helperText,
    required = false,
    children,
    validator,
    width = 'auto',
    maxLength,
    onChange,
    onChangeWithMask,
    onEnterKey,
    onFocusIn,
    validateValue,
    onValidationEvent,
    validateValueMessage,
    placeholder,
    componentSpacing = TextBoxComponentSpacing.StandardSpacing,
    showInfoIcon = false,
    infoText,
}) => {
    const [valueChangeEvent, set_valueChangeEvent] = useState('change');
    const [errorMessage, set_errorMessage] = useState('');
    const [fieldLengthError, set_fieldLengthError] = useState(false);

    const handleValueChanged = (e: ValueChangedEvent) => {
        if (onChange) onChange(e.value);
        if (onChangeWithMask) onChangeWithMask(e.component.option('text'));
        set_valueChangeEvent('input');
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');
        if (onValidationEvent) {
            const isValid = validatedInfo.isValid ?? false;
            onValidationEvent?.(isValid);
        }
    };

    const validationCallback = (options: ValidationCallbackOptions): boolean => {
        if (!validateValue) return true;

        const result = validateValue(options.value?.toString()) ?? false;

        if (onValidationEvent) onValidationEvent(result);

        return result;
    };

    const onKeyDown = (e: any) => {
        const currentValue = e.event.currentTarget.value;
        const key = e.event.key;

        // Allow control keys (Backspace, Tab, Arrow keys, etc.)
        const controlKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
        if (controlKeys.includes(key)) {
            set_fieldLengthError(false);
            return;
        }

        // Detect attempted 5th digit
        if (maxLength && currentValue.length >= maxLength) {
            set_fieldLengthError(true);
            // Prevent the key from being entered
            e.event.preventDefault();
            return;
        }

        set_fieldLengthError(false);
    };

    const onKeyUp = (e: any) => {
        const currentValue = e.event.currentTarget.value;
        // Existing validation logic
        if (validateValue && onValidationEvent) {
            const isValid = validateValue(currentValue);
            onValidationEvent(isValid);
        }
    };

    return (
        <div
            className={`textbox-container ${componentSpacing}`}
            style={{ width: width }}
        >
            {label && (
                <div>
                    <label className={`textbox-label ${errorMessage ? 'error' : ''}`}>{required ? label + ' *' : label}</label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className='textbox-wrapper'
                style={{ position: 'relative' }}
            >
                <DXTextBox
                    id={id}
                    data-testid={dataTestid}
                    mode={type}
                    mask={mask}
                    maskChar={maskChar}
                    maskInvalidMessage={maskInvalidMessage}
                    name={type === 'password' ? 'current-password' : type}
                    inputAttr={{
                        autoComplete: type === 'password' ? 'current-password' : type,
                    }}
                    value={value}
                    valueChangeEvent={valueChangeEvent}
                    disabled={disabled}
                    onValueChanged={handleValueChanged}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    onFocusIn={onFocusIn}
                    readOnly={readOnly}
                    className={`textbox-input ${errorMessage ? 'error' : ''}`}
                    width={width}
                    onEnterKey={onEnterKey}
                    placeholder={placeholder}
                >
                    <Validator onValidated={onValidated}>
                        {required && <RequiredRule message='This field is required' />}
                        {validator}
                        {validateValue && validationCallback && (
                            <CustomRule
                                validationCallback={validationCallback}
                                message={validateValueMessage}
                            />
                        )}
                    </Validator>
                </DXTextBox>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='textbox-error'>{errorMessage}</div>
            ) : maxLength && fieldLengthError ? (
                <div className='textbox-error'>This field will not allow more than {maxLength} characters. </div>
            ) : (
                helperText && <div className='textbox-helper'>{helperText}</div>
            )}
            {children && <div className='textbox-children'>{children}</div>}
        </div>
    );
};
